import React from 'react';
import { stateOptions } from '../commonStateTaxData';

export const basicInfoQuestions = [
  {
    id: 'employee_first_name',
    title: 'First name',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'employee_middle_name',
    title: 'Middle initial',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[a-zA-Z]{1}$',
        customErrorMessage: 'The middle initial can only contain 1 letter.',
      },
    ],
  },
  {
    id: 'employee_last_name',
    title: 'Last name',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'employee_ssn',
    title: 'Social Security number',
    is_required: true,
    placeholder: '9 digits',
    question_type: 'identity',
    identity_type: 'ssn',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^\\d{9}$',
      },
    ],
  },
  {
    id: 'street_address',
    title: 'Home address',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'city_or_town',
    title: 'City or town',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'state',
    title: 'State',
    is_required: true,
    question_type: 'dropdown',
    options: stateOptions,
  },
  {
    id: 'zip_code',
    title: 'Zip code',
    is_required: true,
    question_type: 'short_text',
    placeholder: '5 digits',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^\\d{5}$',
      },
    ],
  },
  {
    id: 'filing_status',
    title: 'Filing status (Line 3)',
    is_required: true,
    question_type: 'long_multiple_choice',
    options: [
      {
        codeValue: 'single_or_head',
        shortName:
          'Single or head of household. Note: If married but legally separated, or spouse is a nonresident alien, check the single box.',
      },
      {
        codeValue: 'married',
        shortName: 'Married',
      },
      {
        codeValue: 'married_withhold_at_single_rate',
        shortName: 'Married, but withholding at a higher single rate',
      },
    ],
  },
];

export const allowanceAndAdditionalAmtQuestions = [
  {
    id: 'total_num_allowances',
    title: (
      <div>
        Total number of allowances you are claiming on the Personal Allowances Worksheet,
        {' '}
        <a href="https://check-api-static-production.s3.amazonaws.com/forms/pdf/me_w4me_05.pdf" target="_blank" rel="noopener noreferrer">
          line E
        </a>
        {' '}
        (Line 4)
      </div>
    ),
    is_required: false,
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[0-9]+(.[0-9]{1,2})?$',
      },
    ],
  },
  {
    id: 'additional_amt_withheld',
    title: 'Additional amount, if any, you want withheld from your paycheck (Line 5)',
    is_required: false,
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[0-9]+(.[0-9]{1,2})?$',
      },
    ],
  },
];

export const worksheetMultSelectQuestion = [
  {
    id: 'applicable_allowances',
    title: 'Check the following boxes that apply to you:',
    is_required: false,
    question_type: 'long_multiple_selection',
    options: [
      {
        codeValue: 'cannot_be_claimed_as_dependent',
        shortName: 'Check for yourself if no one can claim you as a dependent.',
      },
      {
        codeValue: 'married_filing_jointly',
        shortName:
          'Check for your spouse if you will file as married filing jointly. You may choose to not check here if you are married and have either a working spouse or more than one job to help avoid having too little tax withheld.',
      },
      {
        codeValue: 'filing_as_head',
        shortName: 'Check if you will be filing as head of household.',
      },
    ],
  },
];

export const dependentsQuestion = [
  {
    id: 'dependents',
    title:
      'Enter the number of children and dependents eligible for the federal child tax credit or the federal credit for other dependents',
    is_required: true,
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^(0|[1-9][0-9]*)$',
      },
      {
        value: '100',
        action: 'lte',
        category: 'number',
      },
    ],
  },
];

export const additionalAmtWithheldQuestion = [
  {
    id: 'additional_amt_withheld',
    title: 'Additional amount, if any, you want withheld from your paycheck (Line 5)',
    is_required: false,
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[0-9]+(.[0-9]{1,2})?$',
      },
    ],
  },
];

export const exemptionQuestions_12_20 = [
  {
    id: 'exemption_status',
    title:
      'If you do not want any state income tax withheld, select the appropriate option that applies to you.',
    is_required: true,
    question_type: 'long_multiple_choice',
    options: [
      {
        codeValue: 'claimed_exempt_on_w4',
        shortName: 'You claimed "Exempt" on your federal Form W-4',
      },
      {
        codeValue: 'checked_box_on_line_1',
        shortName:
          'You completed federal Form W-4P and checked the box on line 1.',
      },
      {
        codeValue: 'no_tax_liability',
        shortName:
          'You are a resident employee with no Maine tax liability in prior and current years',
      },
      {
        codeValue: 'periodic_retirement_payments',
        shortName:
          'You are a recipient of periodic retirement payments with no tax liability in prior and current years',
      },
      {
        codeValue: 'spouse_is_member_of_military',
        shortName:
          "Your spouse is a member of the military assigned to a location in Maine and you qualify for exemption under the Military Spouse's Residency Relief Act.",
      },
    ],
  },
];

export const exemptionQuestions_12_22 = [
  {
    id: 'exemption_status',
    title:
      'If you do not want any state income tax withheld, select the appropriate option that applies to you. (Line 6)',
    is_required: true,
    question_type: 'long_multiple_choice',
    options: [
      {
        codeValue: 'claimed_exempt_on_w4',
        shortName: 'You claimed "Exempt" on your federal Form W-4',
      },
      {
        codeValue: 'claimed_exempt_on_w4p',
        shortName:
          'You wrote “No Withholding” on your federal Form W-4P',
      },
      {
        codeValue: 'no_tax_liability',
        shortName:
          'You are a resident employee with no Maine tax liability in prior and current years',
      },
      {
        codeValue: 'periodic_retirement_payments',
        shortName:
          'You are a recipient of periodic retirement payments with no tax liability in prior and current years',
      },
      {
        codeValue: 'spouse_is_member_of_military',
        shortName:
          "Your spouse is a member of the military assigned to a location in Maine and you qualify for exemption under the Military Spouse's Residency Relief Act.",
      },
    ],
  },
];
